import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { FiUser, FiCalendar, FiDollarSign, FiCheckCircle, FiXCircle, FiAlertCircle, FiLock } from 'react-icons/fi';
import { ODOO_BASE_URL } from '../../odooConfig';

const FeeVerification = () => {
  const { student_no } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [studentData, setStudentData] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [loginForm, setLoginForm] = useState({ login: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  // Token storage key
  const TOKEN_STORAGE_KEY = 'fee_verification_token';

  // Get stored token
  const getToken = () => {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  };

  // Store token after login
  const setToken = (token) => {
    if (token) {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
      console.log('Token stored in localStorage');
    }
  };

  // Remove token (on logout or expiration)
  const removeToken = () => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    console.log('Token removed from localStorage');
  };

  // Check if user has a token (for authentication check)
  const hasToken = () => {
    return !!getToken();
  };

  const fetchStudentFees = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Debug: Log the URL being requested
      const requestUrl = `${ODOO_BASE_URL}/verify/student/${student_no}`;
      console.log('Fetching student fees from:', requestUrl);
      
      // Get token from storage
      const token = getToken();
      
      if (!token) {
        // No token - user needs to log in
        setLoading(false);
        setShowLogin(true);
        setError({
          type: 'auth_required',
          message: 'Please log in to access fee verification.',
        });
        return;
      }
      
      // Make request with token in Authorization header
      // Token-based authentication - no cookies needed
      const response = await axios.get(
        requestUrl,
        {
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`, // CRITICAL: Include token in header
          },
          timeout: 10000, // 10 second timeout
        }
      );

      // Check if response is HTML (Odoo might return HTML)
      if (typeof response.data === 'string' && response.data.includes('<!DOCTYPE')) {
        // If HTML is returned, we need to parse it or handle it differently
        // For now, show an error suggesting the backend should return JSON
        setError({
          type: 'config_error',
          message: 'The server returned HTML instead of JSON. Please ensure the backend endpoint returns JSON data.',
        });
        return;
      }

      // Handle response data - check for new JSON format
      if (response.data) {
        const data = response.data;
        
        // Check if response follows new format: { success: true, data: {...} }
        if (data.success === false) {
          // Handle error response
          if (data.token_invalid || data.token_expired || data.token_required || 
              data.session_expired || data.requires_login || data.unauthorized) {
            // Token expired, invalid, or requires login - clear token and show login form
            removeToken();
            setLoading(false);
            setShowLogin(true);
            setError({
              type: 'auth_required',
              message: data.error || 'Your session has expired. Please log in again.',
            });
            return;
          } else {
            // Other error
            setError({
              type: 'unknown',
              message: data.error || 'An error occurred while fetching student data.',
            });
            return;
          }
        }
        
        // Success - extract data (handle both old and new format)
        const studentData = data.success ? data.data : data;
        
        // Debug: Log the response structure to understand the data format
        console.log('Fee verification response:', studentData);
        setStudentData(studentData);
        setShowLogin(false); // Hide login form if we successfully got data
        setError(null); // Clear any errors
      }
    } catch (err) {
      // Handle different error types
      if (err.response) {
        const status = err.response.status;
        const data = err.response.data;
        
        // Try to get error message from response data
        const errorMessage = data?.message || data?.error || data?.error_message || '';
        
        // Log response data for debugging
        console.log('Error response data:', { status, data, errorMessage });
        
        // Check for token expiration or invalid token
        if (data?.token_invalid || data?.token_expired || data?.token_required || 
            data?.session_expired || status === 303 || status === 302 || status === 401) {
          // Token expired, invalid, or missing - clear token and show login
          removeToken();
          setLoading(false);
          setShowLogin(true);
          setError({
            type: 'auth_required',
            message: data?.error || 'Your session has expired. Please log in again.',
          });
        } else if (status === 403) {
          // 403 could mean: not authenticated OR authenticated but no permission
          // Since we can't check HttpOnly cookies, we default to showing login form
          // Only show "Access Denied" if response explicitly indicates user IS authenticated
          // (e.g., response includes user object or authenticated: true)
          
          // Check if response explicitly indicates user is authenticated (has user data)
          const isAuthenticatedButNoPermission = (data && typeof data === 'object' && 
                                                 (data.user || data.authenticated === true || data.user_id));
          
          if (isAuthenticatedButNoPermission) {
            // User is authenticated but lacks permission - show access denied
            setError({
              type: 'access_denied',
              message: errorMessage || 'Access Denied. You do not have permission to view this information.',
            });
          } else {
            // Default: show login form for 403
            // This handles: not logged in, invalid session, expired session, etc.
            setLoading(false);
            setShowLogin(true);
            setError({
              type: 'auth_required',
              message: 'Please log in to access fee verification.',
            });
          }
        } else if (status === 404) {
          setError({
            type: 'not_found',
            message: errorMessage || 'Student Not Found. The student number does not exist or the student is archived.',
          });
        } else if (status === 500) {
          // Check if it's a configuration error or general server error
          const isConfigError = errorMessage.toLowerCase().includes('config') || 
                               errorMessage.toLowerCase().includes('setting') ||
                               errorMessage.toLowerCase().includes('category');
          setError({
            type: isConfigError ? 'config_error' : 'unknown',
            message: errorMessage || (isConfigError 
              ? 'Configuration Error. The fee categories have not been selected in settings.'
              : 'Server Error. Please try again later.'),
          });
        } else {
          setError({
            type: 'unknown',
            message: errorMessage || `An error occurred (Status: ${status}). Please try again.`,
          });
        }
      } else if (err.request) {
        // Request was made but no response received
        // This usually means CORS error, server not running, or network issue
        console.error('Network Error Details:', {
          message: err.message,
          code: err.code,
          url: `${ODOO_BASE_URL}/verify/student/${student_no}`,
          config: err.config
        });
        
        let errorMessage = 'Network error. ';
        
        // Check if it's a CORS issue
        if (err.message === 'Network Error' || err.code === 'ERR_NETWORK') {
          errorMessage += 'This might be a CORS issue. Please ensure:\n';
          errorMessage += '1. Odoo server is running on ' + ODOO_BASE_URL + '\n';
          errorMessage += '2. CORS is configured to allow requests from ' + window.location.origin + '\n';
          errorMessage += '3. Check browser console for CORS errors';
        } else {
          errorMessage += 'Please check your connection and try again.';
        }
        
        setError({
          type: 'network_error',
          message: errorMessage,
        });
      } else {
        // Something else happened
        setError({
          type: 'unknown',
          message: err.message || 'An unexpected error occurred. Please try again later.',
        });
      }
    } finally {
      setLoading(false);
    }
  }, [student_no, ODOO_BASE_URL]);

  // Odoo Authentication using custom API endpoint
  const authenticateWithOdoo = async (login, password) => {
    try {
      setLoggingIn(true);
      setLoginError('');

      // Use the custom fee-verification login endpoint
      const response = await fetch(`${ODOO_BASE_URL}/api/fee-verification/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          login: login, // or email/username
          password: password
        })
      });

      // Check if response is OK
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Authentication failed' }));
        setLoginError(errorData.message || `Server error: ${response.status}`);
        return;
      }

      const data = await response.json();

      // Check if authentication was successful
      if (data.success && data.token) {
        // Authentication successful - store the token
        setToken(data.token);
        console.log('Login successful! Token stored.');
        if (data.expires_at) {
          console.log('Token expires at:', data.expires_at);
        }
        
        setShowLogin(false);
        setLoginError('');
        setError(null); // Clear any previous errors
        
        // Now fetch the student fees (token will be included automatically)
        await fetchStudentFees();
      } else {
        setLoginError(data.error || data.message || 'Invalid credentials. Please try again.');
      }
    } catch (err) {
      console.error('Authentication error:', err);
      if (err.message) {
        setLoginError(err.message || 'Failed to connect to server. Please try again.');
      } else {
        setLoginError('Failed to connect to server. Please check your connection and try again.');
      }
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (!loginForm.login || !loginForm.password) {
      setLoginError('Please enter both email and password.');
      return;
    }
    await authenticateWithOdoo(loginForm.login, loginForm.password);
  };

  useEffect(() => {
    if (student_no) {
      // Reset student data when student_no changes to avoid showing stale data
      setStudentData(null);
      setError(null);
      
      // Check if user has a token before making request
      if (hasToken()) {
        // User has token - try to fetch data
        fetchStudentFees();
      } else {
        // No token - show login form immediately
        setLoading(false);
        setShowLogin(true);
        setError({
          type: 'auth_required',
          message: 'Please log in to access fee verification.',
        });
      }
    }
  }, [student_no, fetchStudentFees]);

  const getStatusBadge = (status, balance) => {
    // Determine status based on balance if status is not provided
    let paymentStatus = status;
    if (!paymentStatus && balance !== undefined) {
      if (balance === 0) {
        paymentStatus = 'Paid';
      } else if (balance > 0 && studentData?.fees?.some(f => f.balance < f.amountDue && f.balance > 0)) {
        paymentStatus = 'Partial';
      } else {
        paymentStatus = 'Not Paid';
      }
    }

    switch (paymentStatus?.toLowerCase()) {
      case 'paid':
        return (
          <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-bold bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border-2 border-green-300 shadow-sm">
            <FiCheckCircle className="mr-2 text-green-600" />
            Paid
          </span>
        );
      case 'partial':
        return (
          <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-bold bg-gradient-to-r from-orange-100 to-amber-100 text-orange-800 border-2 border-orange-300 shadow-sm">
            <FiAlertCircle className="mr-2 text-orange-600" />
            Partial
          </span>
        );
      case 'not paid':
      default:
        return (
          <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-bold bg-gradient-to-r from-red-100 to-rose-100 text-red-800 border-2 border-red-300 shadow-sm">
            <FiXCircle className="mr-2 text-red-600" />
            Not Paid
          </span>
        );
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '0.00';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }).format(date);
    } catch (e) {
      return dateString;
    }
  };

  // Show login form if not authenticated (but not if we already have data)
  if (showLogin && !studentData) {
    return (
      <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 min-h-screen flex items-center justify-center xl:pt-[95px] md:pt-[95px] sm:pt-[95px] xs:pt-[75px] px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-10 border border-gray-100 relative overflow-hidden">
            {/* Decorative background */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-100 rounded-full -mr-32 -mt-32 opacity-20"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-indigo-100 rounded-full -ml-24 -mb-24 opacity-20"></div>
            
            <div className="relative z-10 text-center mb-8">
              <div className="bg-gradient-to-br from-[#0863AA] to-[#0a7bc2] p-5 rounded-2xl inline-block mb-5 shadow-lg transform hover:scale-105 transition-transform duration-200">
                <FiLock className="text-white text-4xl" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-3 tracking-tight">Fee Verification Login</h2>
              <p className="text-gray-600 text-base">
                Please log in to verify student fees for <span className="text-[#0863AA] font-bold">{student_no}</span>
              </p>
            </div>

            {error && error.type === 'auth_required' && (
              <div className="mb-6 p-4 bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-300 rounded-xl relative z-10">
                <p className="text-yellow-900 text-sm font-medium flex items-center">
                  <FiAlertCircle className="mr-2 text-yellow-600" />
                  {error.message}
                </p>
              </div>
            )}

            <form onSubmit={handleLoginSubmit} className="space-y-5 relative z-10">
              <div>
                <label htmlFor="login" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  id="login"
                  type="email"
                  value={loginForm.login}
                  onChange={(e) => setLoginForm({ ...loginForm, login: e.target.value })}
                  className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0863AA] focus:border-[#0863AA] transition-all duration-200 bg-gray-50 focus:bg-white"
                  placeholder="portal_user@example.com"
                  required
                  disabled={loggingIn}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  className="w-full px-5 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0863AA] focus:border-[#0863AA] transition-all duration-200 bg-gray-50 focus:bg-white"
                  placeholder="Enter your password"
                  required
                  disabled={loggingIn}
                />
              </div>

              {loginError && (
                <div className="p-4 bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-300 rounded-xl">
                  <p className="text-red-800 text-sm font-medium flex items-center">
                    <FiXCircle className="mr-2 text-red-600" />
                    {loginError}
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={loggingIn}
                className="w-full bg-gradient-to-r from-[#0863AA] to-[#0a7bc2] text-white py-4 rounded-xl font-bold text-lg hover:from-[#075996] hover:to-[#0863AA] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                {loggingIn ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-3"></div>
                    Logging in...
                  </>
                ) : (
                  <>
                    <FiLock className="mr-2" />
                    Log In
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 min-h-screen flex items-center justify-center xl:pt-[95px] md:pt-[95px] sm:pt-[95px] xs:pt-[75px]">
        <div className="text-center">
          <div className="relative inline-block mb-6">
            <div className="animate-spin rounded-full h-20 w-20 border-4 border-blue-200 border-t-[#0863AA]"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <FiDollarSign className="text-[#0863AA] text-2xl animate-pulse" />
            </div>
          </div>
          <p className="text-[#0863AA] text-xl font-semibold mb-2">Loading fee information...</p>
          <p className="text-gray-600 text-sm">Please wait while we fetch the data</p>
        </div>
      </div>
    );
  }

  if (error && !showLogin) {
    const errorIcons = {
      auth_required: '🔐',
      access_denied: '🚫',
      not_found: '🔍',
      config_error: '⚙️',
      network_error: '📡',
      unknown: '❌',
    };

    const errorColors = {
      auth_required: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      access_denied: 'bg-red-50 border-red-200 text-red-800',
      not_found: 'bg-blue-50 border-blue-200 text-blue-800',
      config_error: 'bg-orange-50 border-orange-200 text-orange-800',
      network_error: 'bg-purple-50 border-purple-200 text-purple-800',
      unknown: 'bg-gray-50 border-gray-200 text-gray-800',
    };

    return (
      <div className="bg-gradient-to-br from-[#F2F5F8] to-gray-100 min-h-screen flex items-center justify-center xl:pt-[95px] md:pt-[95px] sm:pt-[95px] xs:pt-[75px] px-4">
        <div className="max-w-2xl w-full">
          <div className={`border-2 rounded-lg p-8 shadow-lg ${errorColors[error.type] || errorColors.unknown}`}>
            <div className="text-center">
              <div className="text-6xl mb-4">{errorIcons[error.type] || errorIcons.unknown}</div>
              <h2 className="text-2xl font-bold mb-4">Error</h2>
              <p className="text-lg mb-6">{error.message}</p>
              {error.type === 'auth_required' && (
                <button
                  onClick={() => setShowLogin(true)}
                  className="inline-block bg-[#0863AA] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#075996] transition-colors"
                >
                  Go to Login
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!studentData) {
    return null;
  }

  // Handle different data structures from backend
  const studentName = studentData.studentName || studentData.name || studentData.student_name || 'N/A';
  const verificationDate = studentData.verificationDate || studentData.verification_date || new Date().toISOString();
  
  // Ensure fees is always an array
  let fees = studentData.fees || studentData.feeData || studentData.data || [];
  
  // If fees is not an array, try to convert it or default to empty array
  if (!Array.isArray(fees)) {
    // If it's an object, try to extract array from common property names
    if (typeof fees === 'object' && fees !== null) {
      // Try to find an array property
      fees = fees.fees || fees.items || fees.list || [];
    } else {
      // If it's not an object or array, default to empty array
      fees = [];
    }
  }

  return (
    <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 min-h-screen xl:pt-[95px] md:pt-[95px] sm:pt-[95px] xs:pt-[75px] pb-16 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header Card - Enhanced */}
        <div className="bg-gradient-to-r from-white to-blue-50 rounded-3xl shadow-2xl p-8 md:p-10 mb-8 border border-blue-100 overflow-hidden relative">
          {/* Decorative background elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-100 rounded-full -mr-32 -mt-32 opacity-20"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-indigo-100 rounded-full -ml-24 -mb-24 opacity-20"></div>
          
          <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex items-center space-x-5">
              <div className="bg-gradient-to-br from-[#0863AA] to-[#0a7bc2] p-5 rounded-2xl shadow-lg transform hover:scale-105 transition-transform duration-200">
                <FiUser className="text-white text-3xl" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2 tracking-tight">{studentName}</h1>
                <p className="text-gray-600 flex items-center text-base">
                  <FiCalendar className="mr-2 text-[#0863AA]" />
                  <span className="font-medium">Student ID: <span className="text-[#0863AA] font-bold">{student_no}</span></span>
                </p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-5 shadow-md hover:shadow-lg transition-shadow duration-200">
              <p className="text-xs text-blue-600 font-semibold mb-2 uppercase tracking-wider">Verification Date</p>
              <p className="text-xl text-blue-900 font-bold">{formatDate(verificationDate)}</p>
            </div>
          </div>
        </div>

        {/* Fees Table Card - Enhanced */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-[#0863AA] via-[#0a7bc2] to-[#0d8fd8] p-6 md:p-8 relative overflow-hidden">
            {/* Decorative pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full -mr-48 -mt-48"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full -ml-32 -mb-32"></div>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-white flex items-center relative z-10">
              <div className="bg-white/20 p-2 rounded-lg mr-4">
                <FiDollarSign className="text-white text-2xl" />
              </div>
              Fee Verification Details
            </h2>
            <p className="text-blue-100 text-sm mt-2 relative z-10">Complete breakdown of all fee categories</p>
          </div>

          {!Array.isArray(fees) || fees.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-gray-500 text-lg">
                {!Array.isArray(fees) 
                  ? 'Fee data format is invalid. Please check the backend response.' 
                  : 'No fee information available.'}
              </p>
              {!Array.isArray(fees) && (
                <p className="text-gray-400 text-sm mt-2">
                  Expected an array but received: {typeof fees}
                </p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                    <th className="px-6 py-5 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Fee Name
                    </th>
                    <th className="px-6 py-5 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Amount Due
                    </th>
                    <th className="px-6 py-5 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Amount Paid
                    </th>
                    <th className="px-6 py-5 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Balance
                    </th>
                    <th className="px-6 py-5 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-5 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Payment Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {fees.map((fee, index) => (
                    <tr 
                      key={index} 
                      className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-200 hover:shadow-sm group"
                    >
                      <td className="px-6 py-5 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900 group-hover:text-[#0863AA] transition-colors">
                          {fee.feeName || fee.name || fee.fee_name || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-5 whitespace-nowrap">
                        <div className="text-sm text-gray-800 font-semibold">
                          ${formatCurrency(fee.amountDue || fee.amount_due || fee.total || 0)}
                        </div>
                      </td>
                      <td className="px-6 py-5 whitespace-nowrap">
                        <div className="text-sm text-green-700 font-semibold flex items-center">
                          <FiCheckCircle className="mr-1 text-green-500" />
                          ${formatCurrency(fee.amountPaid || fee.amount_paid || fee.paid || 0)}
                        </div>
                      </td>
                      <td className="px-6 py-5 whitespace-nowrap">
                        <div className={`text-sm font-bold flex items-center ${
                          (fee.balance || fee.remaining || 0) > 0 ? 'text-red-700' : 'text-green-700'
                        }`}>
                          {(fee.balance || fee.remaining || 0) > 0 ? (
                            <FiAlertCircle className="mr-1 text-red-500" />
                          ) : (
                            <FiCheckCircle className="mr-1 text-green-500" />
                          )}
                          ${formatCurrency(fee.balance || fee.remaining || 0)}
                        </div>
                      </td>
                      <td className="px-6 py-5 whitespace-nowrap">
                        {getStatusBadge(fee.status, fee.balance || fee.remaining)}
                      </td>
                      <td className="px-6 py-5 whitespace-nowrap">
                        <div className="text-sm text-gray-600 font-medium">
                          {formatDate(fee.paymentDate || fee.payment_date || fee.lastPaymentDate)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gradient-to-r from-gray-50 to-gray-100 border-t-4 border-[#0863AA]">
                  <tr>
                    <td colSpan="2" className="px-6 py-5 text-base font-bold text-gray-900">
                      <span className="text-lg">Total</span>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap">
                      <div className="text-base font-bold text-green-700 flex items-center">
                        <FiCheckCircle className="mr-2 text-green-600" />
                        ${formatCurrency(
                          fees.reduce((sum, fee) => sum + (fee.amountPaid || fee.amount_paid || fee.paid || 0), 0)
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap">
                      <div className="text-base font-bold text-red-700 flex items-center">
                        <FiAlertCircle className="mr-2 text-red-600" />
                        ${formatCurrency(
                          fees.reduce((sum, fee) => sum + (fee.balance || fee.remaining || 0), 0)
                        )}
                      </div>
                    </td>
                    <td colSpan="2" className="px-6 py-5"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* Summary Cards - Enhanced */}
        {fees.length > 0 && (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-white to-blue-50 rounded-2xl shadow-xl p-6 md:p-8 border-l-4 border-blue-500 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-200 rounded-full -mr-16 -mt-16 opacity-20 group-hover:opacity-30 transition-opacity"></div>
              <div className="relative z-10 flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-semibold mb-2 uppercase tracking-wide">Total Due</p>
                  <p className="text-3xl font-bold text-gray-900">
                    ${formatCurrency(
                      fees.reduce((sum, fee) => sum + (fee.amountDue || fee.amount_due || fee.total || 0), 0)
                    )}
                  </p>
                </div>
                <div className="bg-blue-100 p-4 rounded-xl group-hover:bg-blue-200 transition-colors">
                  <FiDollarSign className="text-blue-600 text-4xl" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-white to-green-50 rounded-2xl shadow-xl p-6 md:p-8 border-l-4 border-green-500 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-green-200 rounded-full -mr-16 -mt-16 opacity-20 group-hover:opacity-30 transition-opacity"></div>
              <div className="relative z-10 flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-semibold mb-2 uppercase tracking-wide">Total Paid</p>
                  <p className="text-3xl font-bold text-green-700">
                    ${formatCurrency(
                      fees.reduce((sum, fee) => sum + (fee.amountPaid || fee.amount_paid || fee.paid || 0), 0)
                    )}
                  </p>
                </div>
                <div className="bg-green-100 p-4 rounded-xl group-hover:bg-green-200 transition-colors">
                  <FiCheckCircle className="text-green-600 text-4xl" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-white to-red-50 rounded-2xl shadow-xl p-6 md:p-8 border-l-4 border-red-500 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-red-200 rounded-full -mr-16 -mt-16 opacity-20 group-hover:opacity-30 transition-opacity"></div>
              <div className="relative z-10 flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-semibold mb-2 uppercase tracking-wide">Outstanding</p>
                  <p className="text-3xl font-bold text-red-700">
                    ${formatCurrency(
                      fees.reduce((sum, fee) => sum + (fee.balance || fee.remaining || 0), 0)
                    )}
                  </p>
                </div>
                <div className="bg-red-100 p-4 rounded-xl group-hover:bg-red-200 transition-colors">
                  <FiAlertCircle className="text-red-600 text-4xl" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer Note - Enhanced */}
        <div className="mt-10 bg-gradient-to-r from-blue-50 via-indigo-50 to-blue-50 border-2 border-blue-200 rounded-2xl p-6 md:p-8 shadow-md">
          <div className="flex items-start space-x-4">
            <div className="bg-blue-100 p-3 rounded-lg flex-shrink-0">
              <FiAlertCircle className="text-blue-600 text-xl" />
            </div>
            <div>
              <p className="text-blue-900 text-sm md:text-base font-semibold mb-1">Official Verification Document</p>
              <p className="text-blue-700 text-sm">
                This is an official fee verification document. For any discrepancies or questions, 
                please contact the finance department.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeeVerification;
